import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { Building2, CheckCircle2, Loader2, Pencil, Plus } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import {
  ACCOUNTING_REGIME_LABEL,
  InvoiceKindLabel,
  type AccountingRegime,
  type InvoiceKindT,
  type InvoiceSeriesDto,
  type IssuerDto,
} from '@toga/shared'
import { useAuth } from '../../contexts/AuthContext'
import { notifyIssuersChanged } from '../../components/accounting/common'

export default function BillingSettingsPage() {
  const auth = useAuth()
  const isAdmin = auth.status === 'authenticated' && auth.user.role === 'ADMIN'
  const [series, setSeries] = useState<InvoiceSeriesDto[]>([])
  const [issuers, setIssuers] = useState<IssuerDto[]>([])
  const [loading, setLoading] = useState(true)
  const [showNew, setShowNew] = useState(false)
  const [pgcFor, setPgcFor] = useState<IssuerDto | null>(null)
  const [issuerModal, setIssuerModal] = useState<{ open: boolean; issuer: IssuerDto | null }>({
    open: false,
    issuer: null,
  })
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const [s, i] = await Promise.all([
        api.get<{ items: InvoiceSeriesDto[] }>('/invoice-series'),
        api.get<{ items: IssuerDto[] }>('/issuers'),
      ])
      setSeries(s.items)
      setIssuers(i.items)
      notifyIssuersChanged() // el menú y las pantallas de contabilidad dependen del régimen de cada emisor
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const onMakeDefault = async (id: string) => {
    try {
      await api.patch(`/invoice-series/${id}`, { isDefault: true })
      await reload()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error')
    }
  }
  const onToggleActive = async (s: InvoiceSeriesDto) => {
    try {
      await api.patch(`/invoice-series/${s.id}`, { active: !s.active })
      await reload()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error')
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-2xl font-serif font-semibold t-primary">Facturación</h2>
        <p className="t-muted text-sm mt-1">
          Emisores y series. Cada emisor factura con su propio NIF; cada serie pertenece a un emisor
          y tiene su prefijo, contador anual y cadena de huellas propia independientes.
        </p>
      </header>

      {error && (
        <p className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      {/* ===== Emisores (F19) ===== */}
      <section className="surface p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="t-primary font-semibold flex items-center gap-2">
            <Building2 size={15} className="text-toga-300" /> Emisores
          </h3>
          {isAdmin && (
            <button
              type="button"
              onClick={() => setIssuerModal({ open: true, issuer: null })}
              className="px-2.5 py-1 rounded-lg text-xs font-medium text-toga-200 bg-toga-500/15 hover:bg-toga-500/25 flex items-center gap-1"
            >
              <Plus size={12} /> Nuevo emisor
            </button>
          )}
        </div>
        {issuers.length === 0 ? (
          <p className="text-sm t-faint py-3 text-center">Sin emisores.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider t-faint">
              <tr>
                <th className="text-left py-1.5">Nombre</th>
                <th className="text-left">NIF</th>
                <th className="text-center">Por defecto</th>
                <th className="text-center">Estado</th>
                {isAdmin && <th className="w-10"></th>}
              </tr>
            </thead>
            <tbody>
              {issuers.map((i) => (
                <tr key={i.id} className="border-t border-base/40">
                  <td className="py-2 t-primary">
                    {i.name}
                    {i.legalName && <span className="block text-xs t-faint">{i.legalName}</span>}
                    {i.accountingRegime === 'PGC' && (
                      <span className="inline-block mt-0.5 mr-1 text-[10px] px-1.5 py-0.5 rounded-full border border-emerald-500/40 text-emerald-300">
                        Contabilidad PGC
                      </span>
                    )}
                    {i.accountingRegime === 'IRPF_BOOKS' && (
                      <span className="inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded-full border border-toga-500/40 text-toga-300">
                        Libros del autónomo
                      </span>
                    )}
                  </td>
                  <td className="py-2 font-mono t-secondary">{i.nif || '—'}</td>
                  <td className="py-2 text-center">
                    {i.isDefault && <CheckCircle2 size={14} className="inline text-emerald-400" />}
                  </td>
                  <td className="py-2 text-center text-xs">
                    <span className={i.active ? 'text-emerald-300' : 't-faint'}>
                      {i.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  {isAdmin && (
                    <td className="py-2 text-right whitespace-nowrap">
                      {i.active && i.accountingRegime !== 'PGC' && (
                        <button
                          type="button"
                          onClick={() => setPgcFor(i)}
                          className="text-[11px] mr-3 px-2 py-0.5 rounded-full border border-toga-500/40 text-toga-300 hover:bg-toga-500/10"
                          title="Llevar la contabilidad por partida doble en Toga: plan PGC, ejercicios, diario y mayor"
                        >
                          Activar contabilidad PGC
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setIssuerModal({ open: true, issuer: i })}
                        className="t-faint hover:text-toga-300"
                        title="Editar emisor"
                      >
                        <Pencil size={13} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="text-xs t-faint mt-2">
          La facturación del bufete puede verse global o por emisor desde el Dashboard y la página
          de Facturación.
        </p>
      </section>

      {/* ===== Series ===== */}
      <section className="surface p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="t-primary font-semibold">Series</h3>
          {isAdmin && (
            <button
              type="button"
              onClick={() => setShowNew(true)}
              className="px-2.5 py-1 rounded-lg text-xs font-medium text-toga-200 bg-toga-500/15 hover:bg-toga-500/25 flex items-center gap-1"
            >
              <Plus size={12} /> Nueva serie
            </button>
          )}
        </div>

        {loading ? (
          <div className="t-muted flex items-center gap-2 text-sm py-3">
            <Loader2 size={14} className="animate-spin" /> Cargando…
          </div>
        ) : series.length === 0 ? (
          <p className="text-sm t-faint py-3 text-center">Sin series. Crea la primera.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider t-faint">
              <tr>
                <th className="text-left py-1.5">Prefijo</th>
                <th className="text-left">Nombre</th>
                <th className="text-left">Emisor</th>
                <th className="text-left">Tipo</th>
                <th className="text-center">Por defecto</th>
                <th className="text-center">Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {series.map((s) => (
                <tr key={s.id} className="border-t border-base/40">
                  <td className="py-2 font-mono text-toga-300">{s.prefix}</td>
                  <td className="py-2 t-primary">{s.name}</td>
                  <td className="py-2 t-secondary text-xs">{s.issuerName}</td>
                  <td className="py-2 t-secondary text-xs">{InvoiceKindLabel[s.kind]}</td>
                  <td className="py-2 text-center">
                    {s.isDefault ? (
                      <CheckCircle2 size={14} className="inline text-emerald-400" />
                    ) : isAdmin ? (
                      <button
                        type="button"
                        onClick={() => void onMakeDefault(s.id)}
                        className="text-xs t-faint hover:text-toga-300"
                      >
                        Marcar
                      </button>
                    ) : (
                      <span className="t-faint">—</span>
                    )}
                  </td>
                  <td className="py-2 text-center">
                    {isAdmin ? (
                      <button
                        type="button"
                        onClick={() => void onToggleActive(s)}
                        className={`text-xs px-2 py-0.5 rounded border ${
                          s.active
                            ? 'border-emerald-500/30 text-emerald-300 bg-emerald-500/10'
                            : 'border-base t-faint'
                        }`}
                      >
                        {s.active ? 'Activa' : 'Inactiva'}
                      </button>
                    ) : (
                      <span>{s.active ? 'Activa' : 'Inactiva'}</span>
                    )}
                  </td>
                  <td></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {showNew && (
        <NewSeriesModal
          issuers={issuers.filter((i) => i.active)}
          onClose={() => setShowNew(false)}
          onSaved={async () => {
            setShowNew(false)
            await reload()
          }}
        />
      )}

      {pgcFor && (
        <ActivatePgcModal
          issuer={pgcFor}
          onClose={() => setPgcFor(null)}
          onDone={async () => {
            setPgcFor(null)
            await reload()
          }}
        />
      )}

      {issuerModal.open && (
        <IssuerModal
          issuer={issuerModal.issuer}
          onClose={() => setIssuerModal({ open: false, issuer: null })}
          onSaved={async () => {
            setIssuerModal({ open: false, issuer: null })
            await reload()
          }}
        />
      )}
    </div>
  )
}

function NewSeriesModal({
  issuers,
  onClose,
  onSaved,
}: {
  issuers: IssuerDto[]
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  const [prefix, setPrefix] = useState('')
  const [name, setName] = useState('')
  const [kind, setKind] = useState<InvoiceKindT>('ORDINARIA')
  const [issuerId, setIssuerId] = useState(
    issuers.find((i) => i.isDefault)?.id ?? issuers[0]?.id ?? ''
  )
  const [isDefault, setIsDefault] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!prefix.trim() || !name.trim() || !issuerId) {
      setError('Indica prefijo, nombre y emisor')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await api.post('/invoice-series', { prefix, name, kind, isDefault, issuerId })
      await onSaved()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center px-4"
      onClick={onClose}
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md surface-deep p-5 animate-slideUp space-y-3"
      >
        <h3 className="text-base font-semibold t-primary">Nueva serie</h3>

        <label className="block">
          <span className="text-xs t-muted">Emisor (NIF con el que se factura)</span>
          <select
            value={issuerId}
            onChange={(e) => setIssuerId(e.target.value)}
            className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
            required
          >
            {issuers.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name} {i.nif ? `(${i.nif})` : ''}
              </option>
            ))}
          </select>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs t-muted">Prefijo</span>
            <input
              value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
              maxLength={8}
              placeholder="F"
              className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5 font-mono"
              required
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">Tipo</span>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as InvoiceKindT)}
              className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
            >
              {(['ORDINARIA', 'RECTIFICATIVA', 'PROFORMA'] as const).map((k) => (
                <option key={k} value={k}>
                  {InvoiceKindLabel[k]}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="block">
          <span className="text-xs t-muted">Nombre</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Facturas ordinarias 2026"
            className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
            required
          />
        </label>

        <label className="flex items-center gap-2 text-sm t-secondary">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
            className="accent-toga-500"
          />
          Usar como serie por defecto
        </label>

        {error && <p className="text-xs text-rose-300">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Crear
          </button>
        </div>
      </form>
    </div>
  )
}

function IssuerModal({
  issuer,
  onClose,
  onSaved,
}: {
  issuer: IssuerDto | null
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  const [form, setForm] = useState({
    name: issuer?.name ?? '',
    legalName: issuer?.legalName ?? '',
    nif: issuer?.nif ?? '',
    address: issuer?.address ?? '',
    city: issuer?.city ?? '',
    postalCode: issuer?.postalCode ?? '',
    province: issuer?.province ?? '',
    email: issuer?.email ?? '',
    phone: issuer?.phone ?? '',
    bankIban: issuer?.bankIban ?? '',
    bankBic: issuer?.bankBic ?? '',
    invoiceFootnote: issuer?.invoiceFootnote ?? '',
  })
  const [isDefault, setIsDefault] = useState(issuer?.isDefault ?? false)
  const [active, setActive] = useState(issuer?.active ?? true)
  const [regime, setRegime] = useState<AccountingRegime>(issuer?.accountingRegime ?? 'NONE')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const upd = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!form.name.trim() || !form.nif.trim()) {
      setError('Nombre y NIF son obligatorios')
      return
    }
    setBusy(true)
    setError(null)
    const payload = {
      name: form.name.trim(),
      legalName: form.legalName.trim() || null,
      nif: form.nif.trim(),
      address: form.address.trim() || null,
      city: form.city.trim() || null,
      postalCode: form.postalCode.trim() || null,
      province: form.province.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      bankIban: form.bankIban.trim() || null,
      bankBic: form.bankBic.trim() || null,
      invoiceFootnote: form.invoiceFootnote.trim() || null,
      isDefault,
      accountingRegime: regime,
    }
    try {
      if (issuer) {
        await api.patch(`/issuers/${issuer.id}`, { ...payload, active })
      } else {
        await api.post('/issuers', payload)
      }
      await onSaved()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    } finally {
      setBusy(false)
    }
  }

  const inputCls =
    'w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5'

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center px-4 overflow-y-auto py-8"
      onClick={onClose}
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl surface-deep p-5 animate-slideUp space-y-3"
      >
        <h3 className="text-base font-semibold t-primary">
          {issuer ? `Editar emisor — ${issuer.name}` : 'Nuevo emisor'}
        </h3>
        <p className="text-xs t-faint">
          Datos fiscales con los que se emiten las facturas de sus series. Los cambios no afectan a
          facturas ya emitidas (conservan su copia congelada).
        </p>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs t-muted">Nombre comercial *</span>
            <input value={form.name} onChange={upd('name')} className={inputCls} required />
          </label>
          <label className="block">
            <span className="text-xs t-muted">NIF *</span>
            <input
              value={form.nif}
              onChange={upd('nif')}
              className={`${inputCls} font-mono`}
              required
            />
          </label>
          <label className="block col-span-2">
            <span className="text-xs t-muted">Nombre legal completo</span>
            <input value={form.legalName} onChange={upd('legalName')} className={inputCls} />
          </label>
          <label className="block col-span-2">
            <span className="text-xs t-muted">Dirección fiscal</span>
            <input value={form.address} onChange={upd('address')} className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs t-muted">Código postal</span>
            <input value={form.postalCode} onChange={upd('postalCode')} className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs t-muted">Ciudad</span>
            <input value={form.city} onChange={upd('city')} className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs t-muted">Provincia</span>
            <input value={form.province} onChange={upd('province')} className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs t-muted">Email</span>
            <input type="email" value={form.email} onChange={upd('email')} className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs t-muted">Teléfono</span>
            <input value={form.phone} onChange={upd('phone')} className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs t-muted">IBAN (se muestra en facturas)</span>
            <input
              value={form.bankIban}
              onChange={upd('bankIban')}
              className={`${inputCls} font-mono`}
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">BIC</span>
            <input
              value={form.bankBic}
              onChange={upd('bankBic')}
              className={`${inputCls} font-mono`}
            />
          </label>
          <label className="block col-span-2">
            <span className="text-xs t-muted">Pie legal de las facturas (opcional)</span>
            <textarea
              rows={2}
              value={form.invoiceFootnote}
              onChange={upd('invoiceFootnote')}
              className={inputCls}
            />
          </label>
        </div>

        <label className="block">
          <span className="text-xs t-muted">Contabilidad de este emisor</span>
          <select
            value={regime}
            onChange={(e) => setRegime(e.target.value as AccountingRegime)}
            className={inputCls}
          >
            {(Object.keys(ACCOUNTING_REGIME_LABEL) as AccountingRegime[])
              .filter((r) => r !== 'PGC' || issuer?.accountingRegime === 'PGC')
              .map((r) => (
                <option key={r} value={r}>
                  {ACCOUNTING_REGIME_LABEL[r]}
                </option>
              ))}
          </select>
          <span className="block text-[11px] t-faint mt-1">
            «Libros del autónomo» solo para una persona física: activa las pestañas de gastos,
            bienes de inversión y borradores del 303 y el 130 en Contabilidad. La contabilidad por
            partida doble (PGC) se activa con el botón «Activar contabilidad PGC» de la lista.
          </span>
        </label>

        <div className="flex items-center gap-5">
          <label className="flex items-center gap-2 text-sm t-secondary">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              className="accent-toga-500"
            />
            Emisor por defecto
          </label>
          {issuer && (
            <label className="flex items-center gap-2 text-sm t-secondary">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="accent-toga-500"
              />
              Activo
            </label>
          )}
        </div>

        {error && <p className="text-xs text-rose-300">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Guardar
          </button>
        </div>
      </form>
    </div>
  )
}

/** Alta del emisor en el núcleo contable: entidad, plan PGC de PYMES y ejercicios. */
function ActivatePgcModal({
  issuer,
  onClose,
  onDone,
}: {
  issuer: IssuerDto
  onClose: () => void
  onDone: () => void | Promise<void>
}) {
  const thisYear = new Date().getFullYear()
  const options = [thisYear + 1, thisYear, thisYear - 1, thisYear - 2]
  const [years, setYears] = useState<number[]>([thisYear, thisYear - 1])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const activate = async () => {
    setBusy(true)
    setError(null)
    try {
      await api.post(`/issuers/${issuer.id}/pgc`, { years })
      await onDone()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    } finally {
      setBusy(false)
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center px-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md surface-deep p-5 space-y-3"
      >
        <h3 className="text-base font-semibold t-primary">Activar contabilidad PGC</h3>
        <p className="text-xs t-muted">
          {issuer.name} ({issuer.nif}) llevará su contabilidad por partida doble en Toga: se copia
          el plan de cuentas PGC de PYMES y se abren los ejercicios que marques. Se puede repetir
          para añadir ejercicios. El emisor pasa al régimen PGC
          {issuer.accountingRegime === 'IRPF_BOOKS'
            ? ' (sus gastos y bienes de inversión siguen disponibles si es persona física)'
            : ''}
          .
        </p>
        <fieldset className="space-y-1">
          <legend className="text-xs t-muted mb-1">Ejercicios que se abren</legend>
          {options.map((y) => (
            <label key={y} className="flex items-center gap-2 text-sm t-secondary">
              <input
                type="checkbox"
                checked={years.includes(y)}
                onChange={(e) =>
                  setYears((ys) => (e.target.checked ? [...ys, y] : ys.filter((x) => x !== y)))
                }
                className="accent-toga-500"
              />
              {y}
            </label>
          ))}
        </fieldset>
        <p className="text-[11px] text-amber-300">
          Esto no es contabilidad certificada ni sustituye a la gestoría: el plan de cuentas está
          sin contrastar con el BOE.
        </p>
        {error && <p className="text-xs text-rose-300">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={busy || years.length === 0}
            onClick={() => void activate()}
            className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Activar
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
